import React, { useContext, useEffect, useState } from 'react';
import _get from 'lodash/get';
import { Container, Grid, Typography, makeStyles } from '@material-ui/core';
import {
  UserContext,
  UserDispatchContext
} from '../../../Contexts/UserContext';
import ExemptionHeader from '../ExemptionHeader';
import BaseButton from '../../../Components/Buttons/BaseButton';
import Title from '../../../Components/Labels/Title';
import ExemptionHistory from '../ExemptionHistory';
import TimelineSkeleton from '../../../Components/Timeline/TimelineSkeleton';
import NoRevisionIcon from '../../../Components/CustomIcons/NoRevisionIcon';
import { Company } from '../../../Configs/general';
import {
  extractErrorMessage,
  redirectOnAuthFailure
} from '../../../Utils/Errors/Errors';
import { AlertsDispatchContext } from '../../../Contexts/AlertsContext';
import { logoutUser } from '../../../Utils/User/Actions';
import { history } from '../../../Routes/history';
import { ROUTE_NAMES } from '../../../Routes/Routes';
import { GetContractContributionExemptionAPI } from '../../../API/Industrials/IndustrialsAPI';
import { REQUEST_TYPE } from '../../PQR/Pqr_enums';

const redirectToPQR = () => {
  history.push(
    `${ROUTE_NAMES.pqr}?type=${REQUEST_TYPE.ContributionExemption}`,
    {
      modal: false
    }
  );
};

const ExemptionMain = ({ contractId }) => {
  const classes = useStyles();
  const currentUser = useContext(UserContext);
  const authToken = _get(currentUser, 'token');
  const setCurrentUser = useContext(UserDispatchContext);
  const setAlert = useContext(AlertsDispatchContext);

  const [loading, setLoading] = useState(false);
  const [exemptionData, setExemptionData] = useState(null);

  // * Fetch effect
  useEffect(() => {
    let ignoreRequest = false;
    const fetchData = async () => {
      setLoading(true);

      const response = await GetContractContributionExemptionAPI(
        contractId,
        authToken
      );

      if (ignoreRequest) {
        return;
      }

      if (response.success) {
        const exemption = response.data.data.exemption;
        setLoading(false);
        setExemptionData(exemption);
        return;
      }

      if (
        redirectOnAuthFailure(response, '/', () => logoutUser(setCurrentUser))
      ) {
        return;
      }

      const extractedErrors = extractErrorMessage(response);
      setExemptionData(null);
      setLoading(false);
      setAlert({
        type: 'error',
        message: extractedErrors.message
      });
    };

    fetchData();

    return () => {
      ignoreRequest = true;
    };
  }, [authToken, setCurrentUser, setAlert, setLoading, contractId]);

  return (
    <div className={classes.root}>
      <Container maxWidth="md">
        <ExemptionHeader />
        <Grid container className={classes.infoCard}>
          <Typography className={classes.infoTitle}>
            ¿Qué es la Exención de Contribución?
          </Typography>
          <Typography className={classes.infoText} paragraph>
            En Colombia, ciertos usuarios industriales de gas natural pueden
            beneficiarse de{' '}
            <strong>una exención en la contribución especial</strong>{' '}
            establecida en el artículo 102 de la Ley 1450 de 2011. Esta
            contribución, que normalmente es del 8.9% sobre el valor del
            consumo, se destina a subsidiar a usuarios de estratos
            socioeconómicos más bajos. La exención permite a los beneficiarios
            reducir sus costos operativos al no pagar esta contribución
            adicional.
            <br />
            <br />
            La condición para aplicar a este beneficio es que la empresa debe
            tener registrada en el Registro Único Tributario (RUT) una actividad
            económica principal que corresponda a los códigos 011 a 360 o 411 a
            439, según la Resolución 000139 de 2012 de la DIAN.
          </Typography>
        </Grid>
        {!exemptionData && (
          <Title title="Línea de tiempo" className={classes.titleSecond} />
        )}
        {!loading && exemptionData && (
          <ExemptionHistory
            requestExemption={redirectToPQR}
            exemptionData={exemptionData}
            loading={loading}
          />
        )}
        {loading && <TimelineSkeleton />}
        {!exemptionData && !loading && (
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            direction="column"
            className={classes.noExemptionContainer}
          >
            <NoRevisionIcon className={classes.noExemptionIcon} />
            <Grid item>
              <Typography className={classes.noExemptionTitle}>
                {' '}
                No se encontró una exención vigente
              </Typography>
              <Typography className={classes.noExemptionSubtitle}>
                {Company.contractConjugation.capitalized.singular.article}{' '}
                seleccionado no cuenta con ninguna exención vigente en el
                sistema. Si deseas que se aplique la exención a la contribución
                especial de solidaridad, crea una solicitud y revisaremos tu
                caso.
              </Typography>
            </Grid>
            <Grid item>
              <BaseButton
                className={classes.noExemptionButton}
                variant="outlined"
                size="small"
                onClick={redirectToPQR}
              >
                Solicitar Exención
              </BaseButton>
            </Grid>
          </Grid>
        )}
      </Container>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden'
  },
  title: {
    marginTop: theme.spacing(5),
    marginBottom: 30
  },
  titleSecond: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing()
  },
  infoCard: {
    flexWrap: 'wrap',
    backgroundColor: theme.palette.background.cardDark,
    border: 'solid 1px #dbdbdb',
    borderRadius: theme.custom.borderRadius,
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    [`@media (max-width:${theme.breakpoints.values.sm - 1}px)`]: {
      paddingTop: theme.spacing(2.5),
      paddingBottom: theme.spacing(2.5),
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3)
    },
    [`@media (max-width:${theme.breakpoints.values.xm - 1}px)`]: {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    }
  },
  infoTitle: {
    fontSize: 15,
    fontWeight: 500,
    marginBottom: theme.spacing()
  },
  infoText: {
    fontSize: 14,
    textAlign: 'justify'
  },
  actionButton: {
    fontSize: 14,
    color: theme.palette.color.primary,
    marginRight: -theme.spacing(2),
    marginBottom: -theme.spacing()
  },
  noExemptionContainer: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(5)
  },
  noExemptionIcon: {
    height: 210,
    viewBox: '0 0 378.269 250.878',
    marginBottom: theme.spacing(2),
    transform: 'scale(0.75)',
    [theme.breakpoints.up('xm')]: {
      transform: 'scale(0.85)'
    },
    [theme.breakpoints.up('sm')]: {
      transform: 'scale(1)'
    }
  },
  noExemptionTitle: {
    fontSize: 14,
    fontWeight: 500,
    textAlign: 'center'
  },
  noExemptionSubtitle: {
    fontSize: 13,
    fontStyle: 'italic',
    textAlign: 'center',
    maxWidth: 650
  },
  noExemptionButton: {
    marginTop: theme.spacing(3)
  }
}));

export default ExemptionMain;
